﻿/*!
// <copyright file="Util.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/* jshint -W040 */
'use strict';

/* jshint +W040 */

import * as Globalize from "globalize";
import * as $ from "jquery";
import "jquery-ui/ui/tabbable";

export namespace util {

    export interface PropertyBag<TValue> {
        [index: string]: TValue
        [index: number]: TValue
    }

    export interface AddinMessageEvent<TResponse> extends MessageEvent {
        data: TResponse;
        source: Window;
    }

    export interface AddinFeatures {

        /** Indicates if DOM events should be logged; defaults to false. */
        LogDomEvents?: boolean;

        /** Indicates if telemetry should be logged; default to true. */
        LogTelemetry?: boolean;

        /** Indicates whether resource message headers should be refreshed throughout operations. */
        ResourceHeadersRefresh?: boolean;
    }

    export interface FormatODataArgumentValueOptions {

        /** The EDM type of the argument. */
        argumentType?: util.edm;

        /** Whether to omit enum names in OData string. */
        omitEnumMemberQualifier?: boolean;

        /** Whether to permit strings to be null in OData string. */
        allowNullStrings?: boolean;

        /** Whether to encode only a subset of characters, otherwise all characters are encoded. */
        selectivelyEncodeCharacters?: boolean;

        /** Whether to disable extra OData string encoding. */
        disableEncoding?: boolean;
    }

    /**
     * An empty function.
     */
    export function noop(): void {
    }

    export function format(format: string, ...args: any[]): string {
        /// <summary>
        /// Formats a string with an arbitrary number of arguments.
        /// </summary>
        /// <param name="format" type="String">Formatting string that has placholders in the format of: {0}, {1}, ...</param>
        /// <returns type="String">The formatted string.</returns>

        if (format) {
            var formatArgs = args;
            return format.replace(/{(\d+)}/g, function (match, index) {
                var argIndex = Number(index);
                if (!isNaN(argIndex) && formatArgs[argIndex] !== undefined) {
                    var value = formatArgs[argIndex];

                    // Provide nice formatting for objects. If the object overrides
                    // toString(), use that; otherwise use JSON formatting
                    if (typeof value === "object") {
                        if (value && value.hasOwnProperty("toString")) {
                            return value.toString();
                        } else {
                            try {
                                return JSON.stringify(value);
                            } catch (error) {
                                // A circular reference error, use just the value
                                return value;
                            }
                        }
                    } else {
                        return value;
                    }
                } else {
                    return '';
                }
            });
        } else {
            return format;
        }
    }

    /**
     * Determines if the provided object is a function
     *
     * @param obj The object to check
     * @returns true if obj is a function; otherwise, false.
     */
    export function isFunction(obj: any): obj is Function {
        return typeof obj === "function";
    }

    export function getRangeAddressRow(address: string): number {
        /// <summary>
        /// Gets the row number of the provided range address, assuming the address is for a single row.
        /// </summary>
        /// <param name="address" type="String">The address of the range.</param>
        /// <returns type="Number">The row number of the provided range.</returns>

        address = address.split(":")[0];
        let split = address.split("!");
        if (split.length > 1) {
            address = split[1];
        }
        return Number(address.replace(/\D/g, ""));
    }

    export function formatRangeAddress(worksheetName: string, address: string): string {
        /// <summary>
        /// Formats a string representing a range address in a specific worksheet.
        /// </summary>
        /// <param name="worksheetName" type="String">The name of the worksheet containing the range.</field>
        /// <param name="address" type="String">The address of the range.</field>
        /// <returns type="String">The formatted worksheet-specific range address.</returns>

        return $format("{0}!{1}", worksheetName, address);
    }

    export let _window: Window = window;

    export function getOrigin(sourceWindow?: Window): string {
        /// <summary>
        /// Gets the origin of the window.
        /// </summary>
        /// <param name="sourceWindow" type="Window" optional="true">The window for which to get the origin; uses the current window if omitted.</param>
        /// <returns type="String">The origin of the window.</returns>

        if (sourceWindow === undefined) {
            sourceWindow = _window;
        }

        var origin;

        if (sourceWindow.location.origin !== undefined) {
            origin = sourceWindow.location.origin;
        } else {
            var pathIndex = sourceWindow.location.href.indexOf(sourceWindow.location.pathname);
            origin = sourceWindow.location.href.substr(0, pathIndex);
        }

        return origin;
    }

    export function extractHostName(url: string): string {
        /// <summary>
        /// Extracts the host name (e.g., http://web.com) from the provided URL (e.g., http://web.com/path?query=true).
        /// </summary>
        /// <param name="url" type="String">The URL to parse.</param>
        /// <returns type="String">The extracted host name.</returns>

        var parseElement = document.createElement("a");
        parseElement.href = url;
        var protocol = parseElement.protocol;
        var port = parseElement.port;
        var hostName = $format("{0}//{1}", protocol, parseElement.hostname);

        // tslint:disable-next-line:no-http-string
        if (port && ((protocol === "https:" && port !== "443") || (protocol === "http:" && port !== "80"))) {
            hostName += `:${port}`;
        }

        $(parseElement).remove();
        return hostName;
    }

    /// <field type="RegExp">The regular expression used to retrieve a language parameter from the current URL.</field>
    const resourceQueryStringLanguageExpression: RegExp = /[\?|&]Language=([^&]+)/;

    export function getResourceLanguage(): string {
        /// <summary>
        /// Gets a resource's language.
        /// </summary>
        /// <returns type="String">The resource's language.</returns>

        var languageMatches = resourceQueryStringLanguageExpression.exec(_window.location.search);
        if (languageMatches && languageMatches.length === 2) {
            return languageMatches[1];
        }
    }

    export function addStyleSheet(styleSheetPath: string, callback: () => void): void {
        /// <summary>
        /// Adds a new style sheet link to the document for the provided style sheet path.
        /// </summary>
        /// <param name="styleSheetPath" type="String">The path to the style sheet for which to add.</param>
        /// <param name="callback" type="Function">
        /// The void, no-arg callback that will be performed once the style sheet is added and loaded.
        /// </param>

        // Create the link
        var link = $("<link/>", {
            type: "text/css",
            rel: "stylesheet"
        }).appendTo('head');

        // Bind a method that performs the provided callback when the stylesheet is loaded
        link.unbind("load");
        link.bind("load", null, function stylesheetLoaded(event) {
            callback();
        });

        // Set the link's path to the stylesheet
        link.attr("href", styleSheetPath);
    }

    /** ARIA attributes providing accessibility support. */
    export let AriaAttributes = {

        /** Indicates the label that should be used by screen readers for an element. */
        Label: "aria-label",

        /** Indicates the ID of an element providing the label for the containing element. */
        LabelledBy: "aria-labelledby",

        /** Indicates the ID of an element providing the description for the containing element. */
        DescribedBy: "aria-describedby",

        /** Indicates the ID of a descendant element that should be considered active. */
        ActiveDescendant: "aria-activedescendant",

        /** Indicates the element is selected. */
        Selected: "aria-selected",

        /** Indicates the element is expanded. */
        Expanded: "aria-expanded"
    };

    /** ARIA roles providing accessibility support. */
    export let AriaRoles = {

        /** Indicates the element should be considered a grid. */
        Grid: "grid",

        /** Indicates the element should be considered a grid row. */
        Row: "row",
    };

    /* jshint -W069 */
    /// <field type="Object">The application types supported by the app; each member is a bit flag value.</field>
    export enum ApplicationType {

        /// <field type="Number">Bit flag value representing the Excel application.</field>
        Excel = 1,

        /// <field type="Number">Bit flag value representing the Word application.</field>
        Word = 2
    }

    /** The type of client hosting the add-in. */
    export enum ClientType {

        /** Represents an Office application desktop instance. */
        Desktop,

        /** Represents an Office application web instance. */
        Web
    }

    ///  <field type="Object">The versions of Office supported by the app.</field>
    export enum OfficeVersion {

        /// <field type="Number">The legacy version of Office (Office 2013).</field>
        Legacy = 2013,

        /// <field type="Number">The version of Office available at the initial release of Dynamics AX 7 (Office 2016).</field>
        Release = 2016

        // Increment update values as appropriate, by appending decimals, so comparisons can be done properly. (e.g. 2016.1602 for a February update in 2016)
    }

    ///  <field type="Object">The valid message types.</field>
    export enum MessageType {

        /// <field type="String">The status for a critical telemetry response.</field>
        Event = 0,

        /// <field type="String">The status for a error telemetry response.</field>
        Telemetry = 1
    }

    /// <field type="Object">Contains various Edm constants.</field>
    export enum edm {
        binary = "Edm.Binary",
        decimal = "Edm.Decimal",
        double = "Edm.Double",
        int16 = "Edm.Int16",
        int32 = "Edm.Int32",
        int64 = "Edm.Int64",
        single = "Edm.Single",
        string = "Edm.String",
        dateTime = "Edm.DateTimeOffset",
        date = 'Edm.Date',
        guid = "Edm.Guid",
        boolean = 'Edm.Boolean'
    }
    /* jshint +W069 */

    /// <field type="Object">Contains the names of oData related json annotations.</field>
    export let odataJsonAnnotations = {
        eTag: '@odata.etag'
    };

    export function findElementByClass(cssClass: string, parentElement?: JQuery): JQuery {
        /// <summary>Finds an element by it's class.</summary>
        /// <param name="cssClass" type="String">The CSS class assigned to the element to find.</param>
        /// <param name="parentElement" type="JQuery" optional="true">The parent element that contains the element to find.</param>
        /// <returns type="JQuery">The found element.</returns>

        var classSelector = $format(".{0}", cssClass);
        return util.findElement(classSelector, parentElement);
    }

    export function findElementById(elementID: string, parentElement?: JQuery): JQuery {
        /// <summary>Finds an element by it's ID.</summary>
        /// <param name="elementID" type="String">The ID assigned to the element to find.</param>
        /// <param name="parentElement" type="JQuery" optional="true">The parent element that contains the element to find.</param>
        /// <returns type="JQuery">The found element.</returns>

        // Escape all CSS characters in the element ID (otherwise valid IDs like "Microsoft.Dynamics.SomeElement"
        // are parsed incorrectly by jQuery). See the jQuery website for details:
        // http://learn.jquery.com/using-jquery-core/faq/how-do-i-select-an-element-by-an-id-that-has-characters-used-in-css-notation/
        var cssCharacterRegex = /(:|\.|\[|\]|\\|,)/g;
        var escapedElementID = elementID.replace(cssCharacterRegex, "\\$1");

        var idSelector = $format("#{0}", escapedElementID);
        return util.findElement(idSelector, parentElement);
    }

    export function findElement(selector: string, parentElement?: JQuery): JQuery {
        /// <summary>Finds an element using the provided selector.</summary>
        /// <param name="selector" type="String">The selector to use to find the element.</param>
        /// <param name="parentElement" type="jQuery" optional="true">The parent element that contains the element to find.</param>
        /// <returns type="jQuery">The found element.</returns>

        return $(selector, parentElement);
    }

    export interface SetFocusOptions {

        /** Indicates whether the last element should be focused; defaults to false. */
        FocusLast?: boolean;
    }

    /**
     * Sets focus on the provided element if the element can / should receive focus, otherwise focus is set to the first element contained within the element that can receive focus.
     * @param element The element of which to set focus.
     * @param options The options to control how the element is selected.
     */
    export function setFocus(element: JQuery<Element | Document>, options: SetFocusOptions = {}): void {
        if (element && element.length) {
            try {
                if (element.is(":tabbable")) {
                    element.focus();
                } else {
                    const tabbableElements = element.find(":tabbable:not([aria-disabled='true'])");
                    const elementCount = tabbableElements.length;
                    if (elementCount > 0) {
                        $(tabbableElements[options.FocusLast ? elementCount - 1 : 0]).focus();
                    }
                }
            } catch (e) { } // Swallow :tabbable exception - just don't set focus
        }
    }

    export function changeAttribute(control: JQuery<Element>, attribute: string, value?: any): void {
        /// <summary>Changes the provided attribute on the provided control to the provided value, or removes the attribute if the value is undefined.</summary>
        /// <param name="control" type="JQuery">The control to change the attribute on.</param>
        /// <param name="attribute" type="String">The attribute to change.</param>
        /// <param name="value" type="String">The value to change the attribute to.</param>

        if (control) {
            if (value === undefined) {
                control.removeAttr(attribute);
            } else {
                control.attr(attribute, value);
            }
        }
    }

    export function customTooltip(control: Element, tooltipText: string, tooltipClass: string): void {
        /// <summary>Changes the provided attribute on the provided control to the provided value, or removes the attribute if the value is undefined.</summary>
        /// <param name="control" type="JQuery">The control to change the attribute on.</param>
        /// <param name="tooltipText" type="String">TooltipText string for custom tooltip</param>
        /// <param name="tooltipClass" type="String">Class to apply on custom tooltip.</param>
        if (control) {
            control.removeAttribute("title");
            control.classList.add("tooltip");
            var spanControl = document.createElement("SPAN");
            spanControl.innerText = tooltipText;
            spanControl.className = tooltipClass;
            control.appendChild(spanControl);
        }
    }

    export function enableControl(control: JQuery, enable: boolean): void {
        /// <summary>
        /// Enables or disables the provided control, based on the provided enable value.
        /// </summary>
        /// <param name="control" type="JQuery">The control to enable or disable.</param>
        /// <param name="enable" type="Boolean">Indicates if the control should be enabled or disabled.</param>

        var disabledValue;
        if (!enable) {
            disabledValue = "disabled";
        }

        util.changeAttribute(control, "disabled", disabledValue);
    }

    export function controlEnabled(control: JQuery): boolean {
        /// <summary>
        /// Determines if the provided control is enabled.
        /// </summary>
        /// <param name="control" type="jQuery">The control of which to determine if enabled.</param>
        /// <returns type="Boolean">A value indicating if the control is enabled.</returns>

        return control.attr("disabled") !== "disabled";
    }

    export function navigateFrame(frameControl: JQuery, url: string): void {
        /// <summary>Adjusts the provided frame control's src to the provided URL.</summary>
        /// <param name="frameControl" type="JQuery">The frame control to adjust the src for.</param>
        /// <param name="url" type="String">The URL to set the frame control's src to.</param>

        util.changeAttribute(frameControl, 'src', url);
    }

    /**
     * Determines if the provided element is defiend and exists.
     * @param element The element to determine existance for.
     * @returns A value indicating whether the element exists and has a length greater than 0.
     */
    export function doesElementExist(element: JQuery): boolean {
        return element && element.length > 0;
    }

    /** Handles storage and retrieval of key / value pairs. */
    export class Map<T = any> {

        private _nativeMap: globalThis.Map<string, T>;

        constructor() {
            this._nativeMap = new window.Map();
        }

        /**
         * Gets the keys currently stored in the map.
         * @returns The array of keys stored in the map.
         */
        public getKeys(): string[] {
            return Array.from(this._nativeMap.keys());
        }

        /**
         * Determines if the map contains the provided key.
         * @param key The key to check for.
         * @returns A value indicating if the key is contained in the map.
         */
        public containsKey(key: string): boolean {
            return this._nativeMap.has(key);
        }

        /**
         * Determines whether the map is empty.
         * @returns A value indicating if the map is empty.
         */
        public isEmpty(): boolean {
            return this._nativeMap.size === 0;
        }

        /**
         * Adds the provided key / value pair to the map.
         * @param key The key to add.
         * @param value The object to add.
         */
        public add(key: string, value?: T): void {
            this._nativeMap.set(key, value);
        }

        // tslint:disable:no-reserved-keywords
        /**
         * Gets the value corresponding to the provided key.
         * @param key The key to retrieve the value for.
         * @returns The value corresponding to the provided key, or undefined if the key wasn't found.
         */
        public get(key: string): T {
            return this._nativeMap.get(key);
        }
        // tslint:enable:no-reserved-keywords

        /**
         * Removes the provided key and corresponding value from the map.
         * @param key The key to remove.
         * @returns A value indicating if the key / value pair were removed from the map.
         */
        public remove(key: string): boolean {
            if (!this._nativeMap.has(key)) {
                return true;
            }
            return this._nativeMap.delete(key);
        }

        /** Clears the map, removing all key / value pairs. */
        public clear(): void {
            return this._nativeMap.clear();
        }

        /**
         * Gets the values contained in the map.
         * @returns The values contained in the map.
         */
        public getValues(): T[] {
            return Array.from(this._nativeMap.values());
        }

        /**
         * Creates a copy of the map's entries as a PropertyBag
         * @returns A property bag containing all the entries of the map
         */
        public toPropertyBag(): PropertyBag<T> {
            const result = {};
            this._nativeMap.forEach((value, key) => {
                result[key] = value;
            });
            return result;
        }

        /**
         * Copies the values of the map into the provided array.
         * @param array The array to copy the values of the map into.
         */
        public copyToArray(array: T[]): void {
            this._nativeMap.forEach((value, key) => {
                array.push(value);
            });
        }

        /**
         * Copies the values of the map into the provided array.
         * @param array The array to copy the values of the map into.
         * @param callback The void, no argument callback to perform when the copy is complete.
         */
        public copyToArrayAsync(array: T[], callback: () => void): void {
            var self = this;
            new util.BatchProcessor(
                self.getKeys(),
                function processKey(index, key) {
                    array.push(self.get(key));
                },
                { CompleteCallback: callback }).process();
        }

        /**
         * Maps the contents of the map into a new map, calling the provided mapping function to transform the map's values.
         * @param mappingFunction Function that produces a value of the new map.
         * @returns The map containing mapped values.
         */
        public map<TMapped>(mappingFunction: (value: any, key: string, map: Map<T>) => TMapped): util.Map<TMapped> {
            var self = this;
            var transformedMap = new util.Map<TMapped>();
            $.each(self.getKeys(), function (index, key) {
                transformedMap.add(key, mappingFunction(self.get(key), key, self));
            });
            return transformedMap;
        }

        /**
         * Iterates over all values in the map.
         * @param iteratingFunction The function used to iterate over the map. Return true to continue, or false to break.
         */
        public forEach(iteratingFunction: (value: any, key: string, map: Map) => any): void {
            let keepIterating = true;
            this._nativeMap.forEach((value, key, nativeMap) => {
                if (keepIterating) {
                    if (iteratingFunction(value, key, this) === false) {
                        keepIterating = false;
                    }
                }
            });
        }
    }

    export class StringSet {
        /// <summary>
        /// Handles storage and retrieval of unique strings in an unordered collection.
        /// </summary>
        /// <param name="items" type="Array" elementType="String" optional="true">The initial items to add.</param>

        public _set: Object = {};

        constructor(items: string[] = []) {
            this.addAll(items);
        }

        public getItems(): string[] {
            /// <summary>
            /// Gets the items currently stored in the set.
            /// </summary>
            /// <returns type="Array" elementType="String">The array of items stored in the set.</returns>

            return Object.keys(this._set);
        }

        public add(item: string): void {
            /// <summary>
            /// Adds the provided item to the set.
            /// </summary>
            /// <param name="item" type="String">The item to add.</param>

            this._set[item] = undefined;
        }

        public addAll(items: string[]): void {
            /// <summary>
            /// Adds the provided items to the set.
            /// </summary>
            /// <param name="items" type="Array" elementType="String">The items to add.</param>

            var self = this;
            items.forEach(function iterateItems(item, index, array) { self.add(item); });
        }

        public remove(item: string): boolean {
            /// <summary>
            /// Removes the provided item from the set.
            /// </summary>
            /// <param name="item" type="String">The item to remove.</param>
            /// <returns type="Boolean">A boolean value indicating if the item was removed from the set.</returns>

            return delete this._set[item];
        }

        public contains(item: string): boolean {
            /// <summary>
            /// Determines whether the provided item is in the set.
            /// </summary>
            /// <param name="item" type="String">The item to check.</param>
            /// <returns type="Boolean">A value indicating whether the item is in the set.</returns>

            return item in this._set;
        }

        public isEmpty(): boolean {
            /// <summary>
            /// Determines whether the set is empty.
            /// </summary>
            /// <returns type="Boolean">A value indicating if the set is empty.</returns>

            return Object.keys(this._set).length === 0;
        }
    }

    /// <field type="Object">Contains date / time functionality.</field>
    export namespace dateTime {

        /// <field type="Number">Represents the number of seconds in a day.</field>
        export const secondsPerDay = 86400;

        /// <field name="blankDate" type="String">The default date, which indicates that the field is blank</field>
        export const blankDate = '1900-01-01T12:00:00Z';

        /// <field name=blankTimelessDate" type="String">The default date without a time part, which indicates that the field is blank.</field>
        export const blankTimelessDate = '1900-01-01';

        /// <field name="blankDateTime" type="String">The default dateTime, which indicates that the field is blank</field>
        export const blankDateTime = '1900-01-01T00:00:00Z';

        function OLEDateToDateParts(oleDate: number): { Day: number, Month: number, Year: number } {
            /// <summary>
            /// Converts an OLE date to an object containing the Day, Month, and Year.
            /// </summary>
            /// <param name="oleDate" type="Number">The date in the OLE integer format (the number of days since 1900-1-1).</param>
            /// <returns type="Object">An object containing the Day, Month, and Year.</returns>

            // OLE treats 1900 as a leap year, so return 1900-2-29 (the 60th day since 1900-1-1).
            if (oleDate == 60) {
                return { Day: 29, Month: 2, Year: 1900 };
            }

            // If the date is before 1900-2-29, increment to accomodate for OLE issue noted above.
            // Increment all days before it by one to accomodate for this.
            if (oleDate < 60) {
                oleDate++;
            }

            var dateParts: any = {};

            // Convert serial date to year, month, day
            var l = oleDate + 68569 + 2415019;
            var n = Math.floor((4 * l) / 146097);
            l = l - Math.floor((146097 * n + 3) / 4);
            var i = Math.floor((4000 * (l + 1)) / 1461001);
            l = l - Math.floor((1461 * i) / 4) + 31;
            var j = Math.floor((80 * l) / 2447);
            dateParts.Day = l - Math.floor((2447 * j) / 80);
            l = Math.floor(j / 11);
            dateParts.Month = j + 2 - (12 * l);
            dateParts.Year = 100 * (n - 49) + i + l;

            return dateParts;
        }

        export function OLEDateToDateString(oleDate: number): string {
            /// <summary>
            /// Converts an OLE date to a year-month-day format.
            /// </summary>
            /// <param name="oleDate" type="Number">The date in the OLE integer format (the number of days since 1900-1-1).</param>
            /// <returns type="String">A date in YYYY-MM-DD format.</returns>

            var dateParts = OLEDateToDateParts(oleDate);

            return util.dateTime.getDateString(dateParts.Year, dateParts.Month, dateParts.Day);
        }

        export function OLEDateToJSDate(oleDate: number): Date {
            /// <summary>
            /// Converts an OLE date to a JavaScript date.
            /// </summary>
            /// <param name="oleDate" type="Number">The date in the OLE integer format (the number of days since 1900-1-1).</param>
            /// <returns type="Date">A JavaScript date corresponding to the provided OLE date.</returns>

            var dateParts = OLEDateToDateParts(oleDate);

            // Note: months are zero based in JS, so month 11 is December
            return new Date(dateParts.Year, dateParts.Month - 1, dateParts.Day);
        }

        export function dateToOLEDate(year: number, month: number, day: number): number {
            /// <summary>
            /// Converts the parameter year, month, and day to an OLE date.
            /// </summary>
            /// <param name="year" type="Number">The year of the date.</param>
            /// <param name="month" type="Number">The month of the year (0 - 11).</param>
            /// <param name="day" type="Number">The day of the month.</param>
            /// <returns type="Number">The date in the OLE integer format (the number of days since 1900-1-1).</returns>

            // Note: months are zero based in JS, so month 11 is December
            var oleDate = (new Date(year, month, day).getTime() - new Date(1899, 11, 31).getTime()) / 1000 / 60 / 60 / 24;

            // OLE treats 1900 as a leap year, so if the date is greater than 1900-2-29 (the 60th day since 1900-1-1) we need to adjust to accomodate for this.
            if (oleDate >= 60) {
                oleDate++;
            }

            return Math.round(oleDate);
        }

        export function OLETimeToAxTime(oleTime: number): number {
            /// <summary>
            /// Converts an OLE time to AX's time format
            /// </summary>
            /// <param name="oleTime" type="Number">The date in OLE float format (the portion of the day that has passed at this time)</param>
            /// <returns type="Number">The number of seconds that have passed at this time</returns>

            // OLE times are fractions < 1. Remove the whole part of oleTime in case it includes a date.
            var timeFraction = oleTime - Math.floor(oleTime);
            return Math.round(timeFraction * util.dateTime.secondsPerDay);
        }

        export function timeToOLETime(hours: number, minutes: number, seconds: number): number {
            /// <summary>
            /// Converts the specified values into an OLE time.
            /// </summary>
            /// <param name="hours" type="Number">The hours of the time.</param>
            /// <param name="minutes" type="Number">The minutes of the time.</param>
            /// <param name="seconds" type="Number">The seconds of the time.</param>
            /// <returns type="String">The OLE time value.</returns>

            return ((hours * 60 + minutes) * 60 + seconds) / util.dateTime.secondsPerDay;
        }

        export function timeInSecondsToFormat(seconds: number): string {
            /// <summary>
            /// AX's time format to a HH-MM-SS string
            /// </summary>
            /// <param name="seconds" type="Number">The time, given by the number of seconds that have passed in the day</param>
            /// <returns type="String">A HH-MM-SS time string</returns>

            var hours = Math.floor(seconds / 3600);
            seconds = seconds % 3600;
            var minutes = Math.floor(seconds / 60);
            seconds = seconds % 60;


            return util.dateTime.getTimeString(hours, minutes, seconds);
        }

        export function getTimeString(hours: number, minutes: number, seconds: number): string {
            /// <summary>
            /// Format a time in HH-MM-SS format, adding zeros if minutes and seconds are under 10.
            /// </summary>
            /// <param name="hours" type="Number">The hours of the time.</param>
            /// <param name="minutes" type="Number">The minutes of the time.</param>
            /// <param name="seconds" type="Number">The seconds of the time.</param>
            /// <returns type="String">A HH-MM-SS time string.</returns>

            let hoursString = (hours < 10 ? "0" : "") + hours;
            let minutesString = (minutes < 10 ? "0" : "") + minutes;
            let secondsString = (seconds < 10 ? "0" : "") + seconds;

            return `${hoursString}:${minutesString}:${secondsString}`;
        }

        export function getLocalizedTimeString(hours: number, minutes: number, seconds: number): string {
            /// <summary>
            /// Format a time in the localized long time format (e.g. for 'en' it would be hh:mm:ss AM / PM).
            /// </summary>
            /// <param name="hours" type="Number">The hours of the time.</param>
            /// <param name="minutes" type="Number">The minutes of the time.</param>
            /// <param name="seconds" type="Number">The seconds of the time.</param>
            /// <returns type="String">A localized long time representation of the time.</returns>

            return Globalize.format(new Date(0, 0, 0, hours, minutes, seconds), util.dateTime.localizedTimeFormatKey);
        }

        export function getLocalizedDateString(year: number, month: number, day: number): string {
            /// <summary>
            /// Format a date in the localized short date format (e.g. for 'en' it would be yyyy/MM/dd).
            /// </summary>
            /// <param name="year" type="Number">The date's year.</param>
            /// <param name="month" type="Number">The date's month.</param>
            /// <param name="day" type="Number">The date's day.</param>
            /// <returns type="String">A localized short date representation of the date.</returns>

            return Globalize.format(new Date(year, month, day), util.dateTime.localizedDateFormatKey);
        }

        export function jsDateToDisplayValue(jsDate: Date, includeTime: boolean = false, toOLE: boolean = false) {
            /// <summary>
            /// Converts the specified JavaScript Date object to a value suitable for display.
            /// </summary>
            /// <param name="jsDate" type="Date">The JavaScript Date object to convert.</param>
            /// <param name="includeTime" type="Boolean">Indicates whether or not the converted value should include the time component.</param>
            /// <param name="toOLE" type="Boolean" optional="true">Indicates whether the value should be converted to an OLE representation or a human readable representation.</param>
            /// <returns type="Object">
            /// If toOLE is true and the date is for year 1900 or greater, the OLE date representation is returned.
            /// Otherwise, the string representation of the date is returned.
            /// If the JavaScript date is not valid an empty string is returned.
            /// </returns>

            var year = jsDate.getUTCFullYear();
            var displayValue;

            // If the date is valid, convert it to an excel-readable format
            if (!isNaN(year)) {
                var month = jsDate.getUTCMonth();
                var day = jsDate.getUTCDate();
                var utcDateHours = jsDate.getUTCHours();
                var utcDateMinutes = jsDate.getUTCMinutes();
                var utcDateSeconds = jsDate.getUTCSeconds();

                if (toOLE && year >= 1900) {
                    displayValue = util.dateTime.dateToOLEDate(year, month, day);
                    if (includeTime) {
                        displayValue = displayValue + util.dateTime.timeToOLETime(utcDateHours, utcDateMinutes, utcDateSeconds);
                    }
                } else {
                    displayValue = util.dateTime.getLocalizedDateString(year, month, day);
                    if (includeTime) {
                        displayValue = displayValue + " " + util.dateTime.getLocalizedTimeString(utcDateHours, utcDateMinutes, utcDateSeconds);
                    }
                }
            } else {
                // value is invalid
                displayValue = '';
            }

            return displayValue;
        }

        export function getDateString(year: number, month: number, day: number): string {
            /// <summary>
            /// Format a date in YYYY-MM-DD format, adding zeros if month and days are under 10.
            /// </summary>
            /// <param name="year" type="Number">The date's year.</param>
            /// <param name="month" type="Number">The date's month.</param>
            /// <param name="day" type="Number">The date's day.</param>
            /// <returns type="String">A YYYY-MM-DD string.</returns>

            let monthString = (month < 10 ? "0" : "") + month;
            let dayString = (day < 10 ? "0" : "") + day;

            return `${year}-${monthString}-${dayString}`;
        }

        /// <field type="String">The localized date / time format.</field>
        export let _localizedDateTimeFormat = undefined;

        /// <field type="String">The key for the date format pattern.</field>
        export const localizedDateFormatKey = "d";

        /// <field type="String">The key for the time format pattern.</field>
        export const localizedTimeFormatKey = "T";

        /// <value type="String">The localized date / time format.</value>
        export function getLocalizedDateTimeFormat(): string {
            if (_localizedDateTimeFormat === undefined) {
                var patterns = Globalize.culture().calendar.patterns;
                _localizedDateTimeFormat = `${patterns[util.dateTime.localizedDateFormatKey]} ${patterns[util.dateTime.localizedTimeFormatKey]}`;
            }
            return _localizedDateTimeFormat;
        }
    }

    export class Guid {
        /// <summary>
        /// A simple wrapper around the String type that allows GUIDs to be identified.
        /// </summary>
        /// <field name="GuidValue" type="String">The string value of the GUID.</field>

        constructor(public GuidValue: string) { }

        /// <field name="NullGuidString" type="String">The default guid string, which indicates that the guid is blank.</field>
        public static NullGuidString = "00000000-0000-0000-0000-000000000000";
    }

    export class EnumValue {
        /// <summary>
        /// A simple wrapper type that allows enum values to be identified.
        /// </summary>
        /// <field name="EnumName" type="String">The fully-qualified name of the enum.</field>
        /// <field name="EnumMember" type="String">The name of the enum member selected.</field>

        constructor(public EnumName: string, public EnumMember: string) { }
    }

    export function unwrapUtilClasses(value: any): any {
        /// <summary>
        /// Unwraps util.Guid or util.EnumValue objects to retrieve the unwrapped string form of the value.
        /// </summary>
        /// <param name="value" type="Object">The value to unwrap.</param>
        /// <returns type="Object">
        /// If the value passed in is a util.Guid or util.EnumValue, returns the unwrapped string form of the value; otherwise,
        /// returns the value that was passed in.
        /// </returns>

        var unwrappedValue = value;

        if (unwrappedValue instanceof util.Guid) {
            unwrappedValue = unwrappedValue.GuidValue;
        } else if (unwrappedValue instanceof util.EnumValue) {
            unwrappedValue = unwrappedValue.EnumMember;
        }

        return unwrappedValue;
    }

    export function convertODataArgumentValue(argumentValue: any, argumentType: string, options?: FormatODataPartialOptions) {
        /// <summary>
        /// Converts the provided argument value to the JavaScript type corresponding to the provided argument type.
        /// </summary>
        /// <param name="argumentValue" type="Object">The value to convert.</param>
        /// <param name="argumentType" type="util.edm">The type to which to convert the provided value.</param>
        /// <param name="options" type="FormatODataPartialOptions">Optional formatting options to use.</param>
        /// <returns type="Object">The converted type, or undefined if the provided value couldn't be properly converted.</returns>

        var convertedArgumentValue = argumentValue;

        switch (<any>argumentType) {
            case util.edm.string:
                if (typeof (argumentValue) !== 'string') {
                    if (options && options.allowNullStrings && argumentValue === null) {
                        convertedArgumentValue = null;
                    } else {
                        convertedArgumentValue = undefined;
                    }
                } else {
                    convertedArgumentValue = argumentValue;
                }
                break;
            case util.edm.decimal:
            case util.edm.double:
            case util.edm.single:
            case util.edm.int16:
            case util.edm.int32:
            case util.edm.int64:
                convertedArgumentValue = Number(argumentValue);
                if (isNaN(convertedArgumentValue)) {
                    convertedArgumentValue = undefined;
                }
                break;
            case util.edm.date:
            case util.edm.dateTime:
                if (argumentValue instanceof Date) {
                    convertedArgumentValue = argumentValue;
                } else {
                    convertedArgumentValue = new Date(argumentValue);

                    // If value cannot be transformed into a date, just return the original value so
                    // an exception isn't thrown when its toISOString method is called
                    if (isNaN(convertedArgumentValue.valueOf())) {
                        convertedArgumentValue = undefined;
                    }
                }
                break;
            case util.edm.guid:
                if (typeof (argumentValue) === 'string') {
                    convertedArgumentValue = new util.Guid(argumentValue);
                } else if (argumentValue.GuidValue !== undefined) {
                    convertedArgumentValue = argumentValue;
                }
                break;
            case util.edm.boolean:
                if (typeof (argumentValue) === 'string') {
                    convertedArgumentValue = argumentValue.toUpperCase() === 'TRUE';
                }
                break;
            default:
                if (argumentType && typeof (argumentValue) === 'string' && (<any>argumentType).indexOf("Microsoft.Dynamics.") === 0) {
                    convertedArgumentValue = new util.EnumValue(<any>argumentType, argumentValue);
                }
                break;
        }

        return convertedArgumentValue;
    }

    export function formatODataArgumentValue(argumentValue: any, options: FormatODataArgumentValueOptions = {}): any {
        /// <summary>
        /// Formats the provided argument value to the appropriate OData string.
        /// </summary>
        /// <param name="argumentValue" type="Object">The value to format.</param>
        /// <param name="options" type="FormatODataArgumentValueOptions">The formatting options to use.</param>
        /// <returns type="Object">The formatted value, or the provided value if the type didn't need or couldn't be formatted.</returns>

        if (argumentValue !== undefined) {
            if (argumentValue === null && options.allowNullStrings) {
                argumentValue = null;
            } else if (argumentValue instanceof Date) {
                // If the key value is a date, send the ISO string so OData can parse it correctly
                argumentValue = argumentValue.toISOString();

                if (options.argumentType === util.edm.date) {
                    // If the underlying field is Edm.Date - send only the date part
                    argumentValue = argumentValue.replace(/T.*/, "");
                }
            } else if (argumentValue?.GuidValue !== undefined) {
                // If the key value is a GUID, send the literal GUID value to OData
                // Note that custom type information is lost between windows/frames/etc, so we have to check
                // for the existence of the property instead of using "instanceof util.Guid"
                argumentValue = argumentValue.GuidValue;
            } else if (argumentValue?.EnumMember !== undefined) {
                if (options.omitEnumMemberQualifier) {
                    argumentValue = argumentValue.EnumMember;
                } else {
                    // If the key value is an enum, send a formatted, fully-qualified enum value to OData
                    // Note that custom type information is lost between windows/frames/etc, so we have to check
                    // for the existence of the property instead of using "instanceof util.EnumValue"
                    argumentValue = $format("{0}'{1}'", argumentValue.EnumName, argumentValue.EnumMember);
                }
            } else if (typeof argumentValue === 'string' || argumentValue instanceof String) {

                // Before any other formatting, single quotes need to be excaped by doubling them
                argumentValue = argumentValue.replace(/\'/g, "''");

                if (!options.disableEncoding) {
                    if (options.selectivelyEncodeCharacters) {

                        // '%' characters need to be replaced with "%25"
                        argumentValue = argumentValue.replace(/\%/g, "%25");

                        // '&' characters need to be replaced with "%26"
                        argumentValue = argumentValue.replace(/\&/g, "%26");

                        // '+' characters need to be replaced with "%2B"
                        argumentValue = argumentValue.replace(/\+/g, "%2B");

                        // '/' characters need to be replaced with "%2F"
                        argumentValue = argumentValue.replace(/\//g, "%2F");

                        // '?' characters need to be replaced with "%3F"
                        argumentValue = argumentValue.replace(/\?/g, "%3F");

                        // '#' characters need to be replaced with "%23"
                        argumentValue = argumentValue.replace(/\#/g, "%23");
                    } else {

                        // Encode all characters appropriately
                        argumentValue = encodeURIComponent(argumentValue);
                    }

                    // '*' characters need to be replaced with "%2A"
                    argumentValue = argumentValue.replace(/\*/g, "%2A");
                }

                // If the value is a string and and not for a Edm.Date field, surround it in single quotes so string key values with spaces,
                // commas, etc. can be correctly parsed by OData
                if (options.argumentType !== util.edm.date) {
                    argumentValue = `'${argumentValue}'`;
                }
            }
        }

        return argumentValue;
    }

    export interface FormatODataPartialOptions {
        /** Whether to omit enum names in OData string. */
        omitEnumMemberQualifier?: boolean;

        /** Whether to permit strings to be null in OData string. */
        allowNullStrings?: boolean;
    }

    export function convertAndFormatODataArgumentValue(argumentValue: any, fieldType: any, options?: FormatODataPartialOptions): any {
        /// <summary>
        /// Formats the provided argument value to the appropriate OData string after converting it.
        /// </summary>
        /// <param name="argumentValue" type="Object">The value to format.</param>
        /// <param name="fieldType" type="Object">The EDM type of the field of the argument.</param>
        /// <param name="options" type="FormatODataPartialOptions">Optional formatting options to use.</param>
        /// <returns type="Object">The formatted value, or the provided value if the type didn't need or couldn't be formatted.</returns>

        return formatODataArgumentValue(convertODataArgumentValue(argumentValue, fieldType, options), {
            argumentType: fieldType,
            omitEnumMemberQualifier: options && options.omitEnumMemberQualifier,
            allowNullStrings: options && options.allowNullStrings
        });
    }

    /// <field type="Number">The default batch size used when processing array elements.</field>
    const DefaultBatchSize = 2048;

    /// <field type="Number">The number of milliseconds to wait before processing the next batch of array elements.</field>
    const BatchDelay = 10;

    /** The options used to adjust how the batch processor operates. */
    export interface BatchProcessorOptions {

        /** The size of the batch that should be processed before temporarily relinquishing control back to the browser and scheduling the next batch to process. */
        BatchSize?: number;

        /** The callback to perform once the processing of elements has completed. */
        CompleteCallback?: () => void;
    }

    export class BatchProcessor<T> {
        /// <summary>
        /// Handles processing arrays of data in batches, to help avoid the app from becoming unresponsive during processing of large arrays.
        /// </summary>
        /// <field name="_arrayCount" type="Number">The length of the array.</field>
        /// <field name="_lastIndex" type="Number">The last index of the array.</field>
        /// <field name="_rowIndex" type="Number">The index of the current row within the array.</field>

        public _arrayCount: number;
        public _lastIndex: number;
        public _rowIndex: number;
        private _array: any[];
        private _processCallback: (index: number, element: any) => boolean | void;
        private _options: any;

        constructor(array: T[], processCallback: (index: number, element: T) => boolean | void, options: BatchProcessorOptions = {}) {
            /// <summary>
            /// Creates a new instance of the util.BatchProcessor type.
            /// </summary>
            /// <param name="array" type="Array">The array to process.</param>
            /// <param name="processCallback" type="Function">
            /// The callback used to process each array element. The callback will be sent the array element index as a first parameter
            /// and the array element as the second parameter. Returning false breaks out of the loop, stops further processing, and
            /// calls CompleteCallback if provided.
            /// </param>
            /// <param name="options" type="Object" optional="true">
            /// The options used to adjust how the batch processor operates. The follow are the valid options: &#10;
            ///   - BatchSize: The size of the batch that should be processed before temporarily relinquishing control back to the browser and
            ///                scheduling the next batch to process &#10;
            ///   - CompleteCallback: The callback to perform once the processing of elements has completed
            /// </param>

            if (options.BatchSize === undefined) {
                options.BatchSize = DefaultBatchSize;
            }

            if (options.CompleteCallback === undefined) {
                options.CompleteCallback = function noOp() { };
            }

            this._array = array;
            this._processCallback = processCallback;
            this._arrayCount = array.length;
            this._lastIndex = this._arrayCount - 1;
            this._rowIndex = 0;
            this._options = options;
        }

        public process() {
            /// <summary>
            /// Begins processing the array.
            /// </summary>

            if (this._arrayCount > 0) {
                window.setTimeout(() => { this._processNextBatch(); }, 0);
            } else {
                this._options.CompleteCallback();
            }
        }

        private _processNextBatch() {
            /// <summary>
            /// Processes the next batch of array elements.
            /// </summary>

            var breakFromLoop = false;
            var endIndex = this._rowIndex + this._options.BatchSize - 1;

            if (endIndex > this._lastIndex) {
                endIndex = this._lastIndex;
            }

            for (; this._rowIndex <= endIndex; this._rowIndex++) {
                // Break out of loop and stop processing if false is returned from the process callback
                if (this._processCallback(this._rowIndex, this._array[this._rowIndex]) === false) {
                    breakFromLoop = true;
                    break;
                }
            }

            if (this._rowIndex === this._arrayCount || breakFromLoop) {
                this._options.CompleteCallback();
            } else {
                window.setTimeout(() => { this._processNextBatch(); }, BatchDelay);
            }
        }
    }

    /// <field type="Number">The maximum number of iterations the sort async should perform before pausing to allow the browser to handle other pending actions.</field>
    export let MaxIterations = 2048;

    /// <field type="Number">The number of milliseconds to wait before resuming sorting.</field>
    const SortDelay = 10;

    export function sortArrayAsync(array: any[], compareFunction: (a: any, b: any) => number, callback: () => void) {
        /// <summary>
        /// Sorts the array asynchronously using the provided compare function.
        /// </summary>
        /// <param name="array" type="Array">The array to sort.</param>
        /// <param name="compareFunction" type="Function">
        /// The function used to sort the array. The function will be provided an element as a first parameter to compare to second element as a second
        /// parameter. The function should return a value less than 0 if the first element is less than the second element, the function should return 0
        /// if the elements are considered equal to each other, or the function should return a value greater than 0 if the first element is greater than
        /// the second element.
        /// </param>
        /// <param name="callback" type="Function">
        /// The function to perform when the sort has completed. The function should be a void, no argument function.
        /// </param>

        var iterations = 0;

        function partition(startIndex: number, endIndex: number, partitionCallback: () => void, resumedLowerIndex: number = undefined, resumedUpperIndex: number = undefined, resumedPivot: number = undefined, resumedPivotCount: number = undefined) {
            /// <summary>
            /// Handles the sorting of a subset of the array using a quick sort algorithm, which involves sorting and partitioning of the array
            /// until the entire array is sorted. This method also avoid exceeding a max iteration count for a single "working session" to help
            /// alleviate issues with the app being tied up too long and the Apps for Office framework considering the app unresponsive and restarting it.
            /// </summary>
            /// <param name="startIndex" type="Number">The inclusive starting index of the current virtual partition within the array.</param>
            /// <param name="endIndex" type="Number">The inclusive ending index of the current virtual partition within the array.</param>
            /// <param name="partitionCallback" type="Function">The void, no argument callback performed when the partition's partitioning work is complete.</param>
            /// <param name="resumedLowerIndex" type="Number" optional="true">The lower index that should be used to resume processing of the partition.</param>
            /// <param name="resumedUpperIndex" type="Number" optional="true">The upper index that should be used to resume processing of the partition.</param>
            /// <param name="resumedPivot" type="Object" optional="true">The pivot that should be used to resume processing of the partition.</param>
            /// <param name="resumedPivotCount" type="Number" optional="true">The pivot count that should be used to resume processing of the partition.</param>

            // Determine the size of the array based on the provided inclusive start and end indices
            var size = endIndex - startIndex + 1;

            // If the virtual partition has less than two elements, there is nothing to sort as the array is already essentially sorted
            if (size < 2) {
                partitionCallback();
                return;
            }

            // Determine the pivot values along with the lower and upper indices; if there were resumed values provided, use those - otherwise
            // calculate them refresh
            var pivot = resumedPivot !== undefined ? resumedPivot : array[Math.floor((Math.random() * size)) + startIndex];
            var pivotCount = resumedPivotCount !== undefined ? resumedPivotCount : 1;
            var lowerIndex = resumedLowerIndex !== undefined ? resumedLowerIndex : startIndex;
            var upperIndex = resumedUpperIndex !== undefined ? resumedUpperIndex : endIndex;
            var partitionInProgress = false;

            // This function is used to resume sorting, when sorting exceeds the max iterations allowed during a single "working session"
            function resumeSorting() {
                partition(startIndex, endIndex, partitionCallback, lowerIndex, upperIndex, pivot, pivotCount);
            }

            // This function is used to determine if sort has not yet completed; using this function coupled with the sort not complete variable provides some efficiencies
            // so we don't re-determine if the sort has completed more than we need to
            var sortNotComplete;
            function checkSortNotComplete() {
                sortNotComplete = lowerIndex < (upperIndex + 1 - pivotCount);
                return sortNotComplete;
            }
            checkSortNotComplete();

            // Continue partitioning the values within the virtual partition until partitioning is complete or we've exceeded the max number of iterations we should perform
            // in a single "working session"
            while (sortNotComplete) {

                // Move the lower index up until we find an element that should be after the partition (including the pivot itself)
                var lowerIndexCompare;
                while (checkSortNotComplete() && ((lowerIndexCompare = compareFunction(array[lowerIndex], pivot)) < 0)) {
                    iterations++;
                    lowerIndex++;
                }

                // Move the upper index down until we find an element that should be before the partition (including the pivot itself)
                var upperIndexCompare;
                while (checkSortNotComplete() && ((upperIndexCompare = compareFunction(array[upperIndex], pivot)) > 0)) {
                    iterations++;
                    upperIndex--;
                }

                // If the sort isn't complete (indices have overlapped), attempt to swap values between the upper and lower elements appropriately
                if (sortNotComplete) {

                    var upperValue;

                    // If both the lower and upper bounds are currently looking at an element that is considered equal to the pivot element (the combination of the sort not being
                    // complete and that both compares must be 0) attempt to find the first element after the lower bound that is not equal to the pivot and swap it with the
                    // upper bound's element; otherwise either the lower or upper bound's element isn't equal to the pivot element, so swap them
                    if (lowerIndexCompare === upperIndexCompare) {

                        // Determine the next element after the lower bound and before the upper bound that isn't equal to the pivot element; update the pivot count appropriately
                        var tempLowerIndex = lowerIndex + 1;
                        pivotCount = 2;
                        while ((tempLowerIndex < upperIndex) && (compareFunction(array[tempLowerIndex], array[lowerIndex]) === 0)) {
                            iterations++;
                            tempLowerIndex++;
                            pivotCount++;
                        }

                        // Swap the upper bound's element with the first item after the lower bound element that wasn't equal to the pivot; it's possible
                        // that both the temporary lower bound and the upper bound are pointing to the same element, but it's potentially no more performant to
                        // check for that than to just copy the element blindly
                        upperValue = array[upperIndex];
                        array[upperIndex] = array[tempLowerIndex];
                        array[tempLowerIndex] = upperValue;
                        checkSortNotComplete();
                    } else {
                        upperValue = array[upperIndex];
                        array[upperIndex] = array[lowerIndex];
                        array[lowerIndex] = upperValue;
                    }
                }

                // If we've exceed the max iterations we should have performed during this "working session," then pause briefly allowing the browser to handle other pending
                // actions before resuming sorting
                if (iterations >= MaxIterations) {
                    partitionInProgress = true;
                    iterations = 0;
                    window.setTimeout(resumeSorting, SortDelay);
                    break;
                }
            }

            // If the sort is complete, partition the subset of the array below the pivot and the subset of the array above the last element equal to the pivot exclusively;
            // note that if the sort isn't complete it's because we've exceeded the max number of iterations we should have performed during this "working session" so the remainder
            // of the sort work has already been sceduled
            if (!sortNotComplete && !partitionInProgress) {
                partition(startIndex, lowerIndex - 1, function lowerPartitioningComplete() {
                    partition(lowerIndex + pivotCount, endIndex, partitionCallback);
                });
            }
        }

        // Begin partitioning of the entire array
        partition(0, array.length - 1, callback);
    }

    export function generateGuid(): string {
        /// <summary>
        /// Generates a new GUID.
        /// </summary>
        /// <returns type="String">A string representation of the new GUID</returns>
        var guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
        return guid;
    }

    /// <field type="Object">Contains telemetry related definitions and functionality.</field>
    export namespace telemetry {

        /* jshint -W069 */
        /// <field type="Object">The Telemetry type.</field>
        export enum Type {

            /** The status for a exception telemetry response. */
            Exception = "Exception",

            /** The status for a error telemetry response. */
            Error = "Error",

            /** The status for an user error telemetry response. */
            UserError = "UserError",

            /** The status for a system error telemetry response. */
            SystemError = "SystemError",

            /** The status for an Office API error telemetry response. */
            OfficeError = "OfficeError",

            /** The status for a warning telemetry response. */
            Warning = "Warning",

            /** The status for a informational telemetry response. */
            Informational = "Info",

            /**The status for an event that tracks usage and should be marked with EUPI */
            Usage = "Usage",
        }

        /** Represents error severity. */
        export enum ErrorSeverity {

            /** Represents an error with high severity. */
            High,

            /** Represents an error with low severity. */
            Low
        }
        /* jshint +W069 */

        function createTelemetryOptions(telemetryType: util.telemetry.Type, telemetryDetails: any = undefined): any {
            /// <summary>
            /// Creates telemetry options based on the provided telemetry type, optionally including telemetry details.
            /// </summary>
            /// <param name="telemetryDetails" type="Object" optional="true">The telemetry details to include.</param>
            /// <returns type="Object">Telemetry options.</returns>

            var options: any = { MessageType: util.MessageType.Telemetry, TelemetryType: telemetryType };
            if (telemetryDetails !== undefined) {
                options.TelemetryDetails = telemetryDetails;
            }
            return options;
        }

        export function createUsageTelemetryOptions(telemetryDetails: any = undefined): any {
            /// <summary>
            /// Creates Usage level telemetry options, optionally including telemetry details.
            /// </summary>
            /// <param name="telemetryDetails" type="Object" optional="true">The telemetry details to include.</param>
            /// <returns type="Object">Usage level telemetry options.</returns>

            return createTelemetryOptions(util.telemetry.Type.Usage, telemetryDetails);
        }

        export function createInfoTelemetryOptions(telemetryDetails: any = undefined): any {
            /// <summary>
            /// Creates info level telemetry options, optionally including telemetry details.
            /// </summary>
            /// <param name="telemetryDetails" type="Object" optional="true">The telemetry details to include.</param>
            /// <returns type="Object">Info level telemetry options.</returns>

            return createTelemetryOptions(util.telemetry.Type.Informational, telemetryDetails);
        }

        export function createWarningTelemetryOptions(telemetryDetails: any = undefined): any {
            /// <summary>
            /// Creates warning level telemetry options, optionally including telemetry details.
            /// </summary>
            /// <param name="telemetryDetails" type="Object" optional="true">The telemetry details to include.</param>
            /// <returns type="Object">Warning level telemetry options.</returns>

            return createTelemetryOptions(util.telemetry.Type.Warning, telemetryDetails);
        }

        /**
         * Creates user error level telemetry options, optionally including telemetry details.
         * @param telemetryDetails The telemetry details to include.
         * @returns Error level telemetry options.
         */
        export function createUserErrorTelemetryOptions(telemetryDetails: any = undefined): any {
            return createTelemetryOptions(util.telemetry.Type.UserError, telemetryDetails);
        }

        /**
         * Creates system error level telemetry options, optionally including telemetry details.
         * @param telemetryDetails The telemetry details to include.
         * @returns Error level telemetry options.
         */
        export function createSystemErrorTelemetryOptions(telemetryDetails: any = undefined): any {
            return createTelemetryOptions(util.telemetry.Type.SystemError, telemetryDetails);
        }

        /**
         * Creates Office API error level telemetry options, optionally including telemetry details.
         * @param telemetryDetails The telemetry details to include.
         * @returns Error level telemetry options.
         */
        export function createOfficeErrorTelemetryOptions(telemetryDetails: any = undefined): any {
            return createTelemetryOptions(util.telemetry.Type.OfficeError, telemetryDetails);
        }

        export function createErrorTelemetryOptions(telemetryDetails: any = undefined): any {
            /// <summary>
            /// Creates error level telemetry options, optionally including telemetry details.
            /// </summary>
            /// <param name="telemetryDetails" type="Object" optional="true">The telemetry details to include.</param>
            /// <returns type="Object">Error level telemetry options.</returns>

            return createTelemetryOptions(util.telemetry.Type.Error, telemetryDetails);
        }

        function appendStateTelemetryDetails(state: any, telemetryDetails: any, options: any = undefined) {
            /// <summary>
            /// Appends telemetry options based on a state object.
            /// </summary>
            /// <param name="state" type="Object">The state for which to log telemetry.</param>
            /// <param name="telemetryDetails" type="Object">The telemetry details object of which to append state telemetry details.</param>
            /// <param name="options" type="Object" optional="true">
            /// The options that determine how the state should be serialized. Options include the following: &#10;
            /// - IgnoredProperties: the util.StringSet instance containing properties that should be ignored &#10;
            /// - QualifierFormat: the string format used to qualify each property name, where a single format parameter should be specified for the property name
            /// </param>

            if (options === undefined) {
                options = {};
            }

            if (options.IgnoredProperties === undefined) {
                options.IgnoredProperties = new util.StringSet();
            }

            if (options.QualifierFormat === undefined) {
                options.QualifierFormat = "{0}";
            }

            for (var propertyName in state) {
                var qualifiedPropertyName = $format(options.QualifierFormat, propertyName);
                var property = state[propertyName];
                if (!options.IgnoredProperties.contains(propertyName) && !isFunction(property)) {
                    switch (typeof property) {
                        case "object":
                            appendStateTelemetryDetails(property, telemetryDetails, { QualifierFormat: $format("{0}.{1}", qualifiedPropertyName, "{0}") });
                            break;
                        case "string":
                            telemetryDetails[qualifiedPropertyName] = property;
                            break;
                        default:
                            telemetryDetails[qualifiedPropertyName] = JSON.stringify(property);
                            break;
                    }
                }
            }
        }

        /// <field type="Array" elementType="String">The array of DOM events that should log telemetry.</field>
        const telemetryWrittenDomEvents = ["click", "dblclick"];

        export function configureDomTelemetry(logTelemetryCallback: (event: string, telemetryOptions: any) => void) {
            /// <summary>
            /// Configures telemetry to be written for DOM events.
            /// </summary>
            /// <param name="logTelemetryCallback" type="Function">
            /// The function used to write telemetry. The function takes the event as the first parameter, and an options object as the second parameter that includes telemetry options.
            /// </param>

            var domTelemetryHandler = new util.telemetry._DomTelemetryHandler(logTelemetryCallback);

            for (const eventType of telemetryWrittenDomEvents)
            {
                $(document).on(eventType, domTelemetryHandler.logEventTelemetry);
            }
        }

        /// <field type="String">The selector that indicates what elements should log telemetry.</field>
        const telemetryWrittenDomSelector = "button, a, li";

        /// <var type="util.StringSet">The attributes that should be ignored, and not written to telemetry.</var>
        const ignoredAttributes = new util.StringSet(["title", "href", "disabled", "value", "data-value", "aria-label"]);

        export class _DomTelemetryHandler {
            /// <summary>
            /// Handles writing DOM events to telemetry.
            /// </summary>
            /// <param name="logTelemetryCallback" type="Function">
            /// The function used to write telemetry. The function takes the event as the first parameter, and an options object as the second parameter that includes telemetry options.
            /// </param>

            /* jshint -W040 */
            constructor(private logTelemetryCallback: (event: string, telemetryOptions: any) => void) {
                this.logEventTelemetry = this.logEventTelemetry.bind(this);

            }
            /* jshint +W040 */

            public logEventTelemetry(event: JQuery.TriggeredEvent): void {
                /// <summary>
                /// Writes DOM events to telemetry.
                /// </summary>
                /// <param name="event" type="jQuery.Event">
                /// The document's event that was triggered.
                /// </param>

                var target: any = event.originalEvent ? event.originalEvent.target : event.target;
                var $target = $(target);

                // If the element that triggered the event isn't an element that should log telemetry, but is a direct child of a element that should, then adjust the target to indicate the parent instead of the child
                if (!$target.is(telemetryWrittenDomSelector) && $target.parent().is(telemetryWrittenDomSelector)) {
                    $target = $target.parent();
                    target = $target[0];
                }

                // Only log telemetry if the target is an element that should log telemetry
                if ($target.is(telemetryWrittenDomSelector)) {

                    var telemetryDetails = {
                        Event: event.type,
                        Type: target.nodeName,
                        Path: $target.parents().addBack().map(function (index, element) { return element.id ? $format("{0}[id=\"{1}\"]", element.nodeName, element.id) : element.nodeName; }).get().join("\\")
                    };

                    // Add all attributes that aren't indicated to be ignored to the telemetry details as individual properties
                    var attributes = target.attributes;
                    if (attributes) {
                        for (var index = 0; index < attributes.length; index++) {
                            var attribute = attributes[index];
                            if (!ignoredAttributes.contains(attribute.nodeName)) {
                                telemetryDetails[attribute.nodeName] = attribute.nodeValue || "";
                            }
                        }
                    }

                    // Log the telemetry
                    this.logTelemetryCallback("dom-event", util.telemetry.createInfoTelemetryOptions(telemetryDetails));
                }
            }
        }
    }

    export class Promise {
        /// <summary>
        /// Represents a promise object, where upon successful execution a provided then callback is executed; if execution fails, a provided
        /// catch callback is executed.
        /// </summary>
        /// <field name="_then" type="util.Promise_NextClause">The next then clause to execute upon sucess.</field>
        /// <field name="_catch" type="util.Promise_NextClause">The next catch clause to execute upon failure.</field>
        /// <field name="_finally" type="util.Promise_NextClause">The next finally clause to execute after any successful or failed executions complete.</field>

        public _then: util.Promise_NextClause;
        public _catch: util.Promise_NextClause;
        public _finally: util.Promise_NextClause;

        // tslint:disable-next-line:no-reserved-keywords
        public then(callback: ((result: any) => any) | { catch?: (error: any) => void, finally?: () => void }, options: any = undefined): util.Promise {
            /// <summary>
            /// This method will be called once the previous promise has been resolved.
            /// </summary>
            /// <param name="callback" type="Function" optional="true">
            /// The callback to perform once the previous promise has been resolved. If the callback returns a promise object, it is assumed the callback has initiated an async process,
            /// and that process will continue the promise chain; the next 'then', 'catch', and 'finally' clauses are set to on any returned promise objects to facilitate chaining. The callback
            /// is sent a single parameter representing the result used to resolve the promise.
            /// </param>
            /// <param name="options" type="Object" optional="true">
            /// The optional additional handlers to provide. Valid options include the following: &#10;
            ///   - catch: Catches failures or exceptions from actions within the promise, or from an unhandled exception earlier in the call stack.  &#10;
            ///   - finally: Executes after the success or catch handlers, ensuring the handler is always called during its promise chain.
            /// </param>
            /// <returns type="util.Promise">A promise object allowing for further chaining.</returns>

            switch (arguments.length) {
                case 0:
                    callback = $.noop;
                    options = {};
                    break;
                case 1:
                    if (isFunction(callback)) {
                        options = {};
                    } else {
                        callback = $.noop;
                        options = arguments[0];
                    }
                    break;
            }

            this._then = new util.Promise_NextClause(<any>callback, new util.Promise());

            if (isFunction(options.catch)) {
                this.catch(options.catch);
            }

            if (isFunction(options.finally)) {
                this.finally(options.finally);
            }

            return this._then.Promise;
        }

        // tslint:disable-next-line:no-reserved-keywords
        public catch(callback: (error: any) => any): util.Promise {
            /// <summary>
            /// Catches failures or exceptions from actions within the promise, or from an unhandled exception earlier in the call stack.
            /// </summary>
            /// <param name="callback" type="Function">
            /// The callback to perform once the previous promise has been rejected. If the callback returns a promise object, it is assumed the callback has initiated an async process,
            /// and that process will continue the promise chain; the next 'then' and 'catch' clauses are set to on any returned promise objects to facilitate chaining. The callback is sent a single
            /// parameter representing the error encountered.
            /// </param>
            /// <returns type="util.Promise">A promise object allowing for further chaining.</returns>

            this._catch = new util.Promise_NextClause(callback, new util.Promise());

            return this._catch.Promise;
        }

        // tslint:disable-next-line:no-reserved-keywords
        public finally(callback: () => any): util.Promise {
            /// <summary>
            /// Executes after the success or catch handlers, ensuring the handler is always called during its promise chain.
            /// </summary>
            /// <param name="callback" type="Function">
            /// The callback to perform once the previous promise has been resolved or rejected. If the callback returns a promise object, it is assumed the callback has initiated an async process,
            /// and that process will continue the promise chain; the next 'then', 'catch', and 'finally' clauses are set to on any returned promise objects to facilitate chaining. The callback
            /// is sent a single parameter representing the result used to resolve the promise. The callback is sent no parameters.
            /// </param>
            /// <returns type="util.Promise">A promise object allowing for further chaining.</returns>

            this._finally = new util.Promise_NextClause(callback, new util.Promise());

            return this._finally.Promise;
        }

        public resolve(result: any = undefined) {
            /// <summary>
            /// Fulfills the current promise, executing the next 'then' clauses if present.
            /// </summary>
            /// <param name="result" type="Object" optional="true">The result with which to resolve the promise.</param>

            if (this._then !== undefined) {
                util.Promise._executePromiseCallback(this, this._then, result);
            } else if (this._finally !== undefined) {
                util.Promise._executePromiseCallback(this, this._finally);
            }
        }

        public reject(reason: any): void {
            /// <summary>
            /// Rejects the current promise, executing the closest catch clauses down the stack.
            /// </summary>
            /// <param name="reason" type="Object">The reason with which to reject the promise.</param>

            util.Promise._executeClosestPromiseCatch(this, reason, { isException: false });
        }

        private static _executePromiseCallback(promise: util.Promise, nextObject: util.Promise_NextClause, data: any = undefined) {
            /// <summary>
            /// Executes the next promise callback, using the next clause object and its corresponding data.
            /// </summary>
            /// <param name="promise" type="util.Promise">The promise containing the provided next object.</param>
            /// <param name="nextObject" type="util.Promise._NextClause">The next object clause to execute.</param>
            /// <param name="data" type="Object" optional="true">The data to send to the next clause object's callback.</param>

            try {

                // Execute the provided callback
                var result = nextObject.Callback(data);

                // If the promise has a finally clause (and its not currently executing a finally clause), execute
                // the finally clause before processing the next 'then' clause
                if (promise._finally !== undefined && promise._finally !== nextObject) {
                    util.Promise._executePromiseCallback(promise, promise._finally);
                }

                // If the result was a promise object, assume that there is a separate handler for that promise object, and just assign the remainder of this promise call stack to
                // the returned promise; otherwise, execute the promise's 'then' clause if present.
                if (result instanceof util.Promise) {
                    result._then = nextObject.Promise._then;
                    result._catch = nextObject.Promise._catch;
                    result._finally = nextObject.Promise._finally;
                } else {

                    // If the next promise has a then clause, execute it; otherwise, check to see if the next promise has a finally clause and execute it instead.
                    if (nextObject.Promise._then !== undefined) {
                        util.Promise._executePromiseCallback(nextObject.Promise, nextObject.Promise._then, result);
                    } else if (nextObject.Promise._finally !== undefined) {
                        util.Promise._executePromiseCallback(nextObject.Promise, nextObject.Promise._finally);
                    }
                }
            } catch (e) {

                // An error occurred, so execute the next catch clause found in the call stack
                util.Promise._executeClosestPromiseCatch(nextObject.Promise, e, { finally: promise._finally });
            }
        }

        private static _executeClosestPromiseCatch(promise: util.Promise, reason: any, options: any = undefined) {
            /// <summary>
            /// Finds the closes catch clause starting for the provided promise. The closest catch clause if found by traversing through the 'then' clauses' promises and checking each for the
            /// presence of a 'catch' clause.
            /// </summary>
            /// <param name="promise" type="util.Promise">The promise from which to begin the search for catch clauses.</param>
            /// <param name="reason" type="Object">The reason with which to reject the promise.</param>
            /// <param name="options" type="Object">
            /// The optional additional handlers to provide. Valid options include the following: &#10;
            ///   - finally: The finally object clause to execute. &#10;
            ///   - isException: Indicates if the reason is an exception; if omitted, the reason is assumed to be an exception.
            /// </param>

            if (options === undefined) {
                options = {};
            }

            var executeFinally;
            if (options.finally !== undefined) {
                executeFinally = function finalize() {
                    util.Promise._executePromiseCallback(promise, options.finally);
                };
            } else {
                executeFinally = $.noop;
            }

            if (promise._catch !== undefined) {
                util.Promise._executePromiseCallback(promise, promise._catch, reason);
            } else {
                executeFinally();
                if (promise._then !== undefined) {
                    util.Promise._executeClosestPromiseCatch(promise._then.Promise, reason, { finally: promise._finally, isException: options.isException });
                } else {
                    if (promise._finally !== undefined) {
                        util.Promise._executePromiseCallback(promise, promise._finally);
                    }

                    if (options.isException !== false) {
                        throw reason;
                    }
                }
            }
        }
    }

    export class Promise_NextClause {
        constructor(public Callback: Function, public Promise: util.Promise) { }
    }

    /** Contains generic classes. */
    export namespace generics {

        /** Handles storage and retrieval of key / value pairs. */
        export let Map = util.Map;
    }
}

// Aliases for common functions
export var $format = util.format;

// Ensure window.location.origin is always present
////if (!window.location.origin) {
////    window.location.origin = util.getOrigin(window);
////}
